import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { Section } from "@/components/Section";
import { RelatedReading } from "@/components/RelatedReading";
import { AiAnswersCallout } from "@/components/AiAnswersCallout";
import type { Location } from "@/content/locations";
import { SERVICES } from "@/content/services";
import { RANGPUR_AREAS } from "@/content/rangpur-areas";
import { PROGRAMMABLE_SERVICE_SLUGS } from "@/content/city-services";
import { nearbyCities } from "@/lib/geo-links";

/** Internal-link sections of a city hub: nearby cities, reading, service pages, Rangpur areas. */

/**
 * Per-city industry bridge — links a location's strongest vertical to the
 * matching /seo-for/$industry page so visitors flow from geo intent to
 * industry intent in one click.
 */
const CITY_INDUSTRY: Record<string, { slug: string; label: string }> = {
  dhaka: { slug: "saas", label: "SaaS SEO playbook" },
  chittagong: { slug: "ecommerce", label: "Ecommerce SEO playbook" },
  sylhet: { slug: "restaurants", label: "Restaurant SEO playbook" },
  rangpur: { slug: "healthcare", label: "Healthcare SEO playbook" },
};

/**
 * Bangladesh-aware local-SEO reading list rendered on every location page.
 * Hrefs must resolve through `src/content/internal-links.ts`.
 */
const LOCAL_INSIGHTS_HREFS = [
  "/blog/google-business-profile-optimization-checklist-2026",
  "/blog/local-citation-building-bangladesh-27-directories",
  "/blog/state-of-seo-bangladesh-2026",
  "/blog/bangla-language-seo-bd-search-demand",
  "/blog/tier-2-bangladesh-seo-sylhet-khulna-rajshahi",
  "/blog/review-generation-google-filter-compliant",
];

const CITY_AI_QUESTIONS = [
  "local-seo-still-matter",
  "seo-bangladesh-market",
  "bangla-seo-worth-it",
  "seo-cost-bangladesh",
];

export function CityHubNearby({ loc }: { loc: Location }) {
  const others = nearbyCities(loc.slug, 6);
  const industry = CITY_INDUSTRY[loc.slug];
  return (
    <Section
      eyebrow="Other cities"
      title={
        <>
          SEO services in <span className="text-gradient">nearby areas.</span>
        </>
      }
    >
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {others.map(({ loc: o, km }) => (
          <Link
            key={o.slug}
            to="/seo-services/$location"
            params={{ location: o.slug }}
            className="group rounded-3xl border border-border p-6 bg-card hover:border-primary transition"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  {o.region} · {km} km away
                </div>
                <div className="mt-1 font-display text-xl">SEO Expert in {o.city}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  {o.industries.slice(0, 3).join(", ")}.
                </p>
                <span className="mt-3 inline-flex flex-wrap gap-x-2 gap-y-1 text-xs text-muted-foreground">
                  Local SEO · Technical audit · AEO &amp; GEO
                </span>
              </div>
              <ArrowUpRight
                size={18}
                className="mt-1 shrink-0 text-muted-foreground group-hover:text-primary"
              />
            </div>
          </Link>
        ))}
      </div>
      {industry && (
        <div className="mt-10 rounded-3xl border border-primary/30 bg-card p-6 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground max-w-xl">
            {loc.slug === "rangpur"
              ? "Running a clinic or healthcare service? Explore a dedicated SEO planning guide."
              : `${loc.city} businesses lean heavily on one vertical — see the dedicated playbook for it.`}
          </p>
          <Link
            to="/seo-for/$industry"
            params={{ industry: industry.slug }}
            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium"
          >
            {industry.label} <ArrowUpRight size={14} />
          </Link>
        </div>
      )}
    </Section>
  );
}

/** AI-answers callout plus the local-SEO reading list. */
export function CityHubReading({ loc }: { loc: Location }) {
  return (
    <>
      <Section className="!pt-0">
        <AiAnswersCallout context={`${loc.city} SEO`} questionSlugs={CITY_AI_QUESTIONS} />
      </Section>

      <RelatedReading
        eyebrow={`Local SEO insights · ${loc.city}`}
        title={
          <>
            Bangladesh-aware <span className="text-gradient">local SEO reading.</span>
          </>
        }
        description={
          loc.slug === "rangpur"
            ? "Practical guides to Business Profiles, accurate listings, genuine reviews and Bangla content planning."
            : `The same playbooks I use for ${loc.city} clients — Google Business Profile, citations, reviews, and Bangla search demand.`
        }
        hrefs={LOCAL_INSIGHTS_HREFS}
      />
    </>
  );
}

/** Links to every city × service page for this city. */
export function CityHubServicePages({ loc }: { loc: Location }) {
  return (
    <Section
      eyebrow={`${loc.city} · services`}
      title={
        <>
          Pick a <span className="text-gradient">{loc.city} playbook.</span>
        </>
      }
    >
      <p className="max-w-2xl text-muted-foreground -mt-6 mb-8">
        {loc.slug === "rangpur"
          ? "Explore the scope and deliverables for the service your business needs."
          : `Dedicated pages for the specific SEO programs ${loc.city} businesses ask about most — each with the city-specific angle, deliverables, and FAQs.`}
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {PROGRAMMABLE_SERVICE_SLUGS.map((slug) => {
          const s = SERVICES.find((x) => x.slug === slug);
          if (!s) return null;
          return (
            <Link
              key={slug}
              to="/seo-services/$location/$service"
              params={{ location: loc.slug, service: slug }}
              className="group rounded-3xl border border-border p-6 bg-card hover:border-primary transition"
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    {loc.city}
                  </div>
                  <div className="mt-1 font-display text-lg leading-snug">{s.title}</div>
                </div>
                <ArrowUpRight
                  size={16}
                  className="text-muted-foreground group-hover:text-primary"
                />
              </div>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.tagline}</p>
            </Link>
          );
        })}
      </div>
    </Section>
  );
}

/** Rangpur-only grid linking every hyperlocal area page. */
export function CityHubRangpurAreas() {
  return (
    <Section
      eyebrow="Hyperlocal · Rangpur"
      title={
        <>
          SEO for every <span className="text-gradient">corner of Rangpur.</span>
        </>
      }
    >
      <p className="max-w-2xl text-muted-foreground -mt-6 mb-8">
        Explore planning examples for neighborhoods, intersections, and bazaars—from Medical Mor to
        Begum Rokeya University, Jahaj Company More to Haragash. Only target areas your business
        genuinely serves.
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {RANGPUR_AREAS.map((a) => (
          <Link
            key={a.slug}
            to="/seo-services/rangpur/$area"
            params={{ area: a.slug }}
            className="group rounded-3xl border border-border p-5 bg-card hover:border-primary transition"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  Rangpur
                </div>
                <div className="mt-1 font-display text-lg leading-tight">SEO near {a.name}</div>
              </div>
              <ArrowUpRight size={16} className="text-muted-foreground group-hover:text-primary" />
            </div>
          </Link>
        ))}
      </div>
    </Section>
  );
}
